/**
 * Agreements Page
 *
 * Employee page to view all agreements from evaluations.
 * Shows agreements grouped by evaluation period.
 */

import { Layout } from '../../components/Layout';
import { useAuth } from '../auth';
import { useEmployeeAgreements } from './useAgreements';
import { AgreementsByPeriod } from './AgreementsByPeriod';
import { AgreementCard } from './AgreementCard';

export function AgreementsPage() {
  const { user } = useAuth();

  const { data, isLoading, error } = useEmployeeAgreements(user?.employeeId || 0);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto" data-testid="agreements-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Mijn Afspraken</h1>
          <p className="mt-2 text-gray-600">
            Overzicht van alle afspraken uit je beoordelingsgesprekken.
          </p>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Afspraken laden...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h3 className="text-lg font-medium text-red-900">Fout bij laden afspraken</h3>
                <p className="mt-1 text-sm text-red-700">
                  Er is een fout opgetreden bij het laden van je afspraken. Probeer het later opnieuw.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        {!isLoading && !error && data && (
          <>
            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{data.data.length}</div>
                    <div className="text-sm text-gray-500">Totaal Afspraken</div>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">
                      {data.data.filter(a => a.isCompleted).length}
                    </div>
                    <div className="text-sm text-gray-500">Afgerond</div>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">
                      {data.data.filter(a => !a.isCompleted).length}
                    </div>
                    <div className="text-sm text-gray-500">In Uitvoering</div>
                  </div>
                </div>
              </div>
            </div>

            {/* View Toggle */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Afspraken per Periode</h2>
              <div className="text-sm text-gray-500">
                {Object.keys(data.byEvaluation).length} evaluatieperiode(s)
              </div>
            </div>

            {/* Agreements grouped by period */}
            <AgreementsByPeriod agreementsByEvaluation={data.byEvaluation} />

            {/* Empty state for when no agreements are grouped */}
            {data.data.length === 0 && (
              <div className="text-center py-12 bg-white border border-gray-200 rounded-lg">
                <svg
                  className="mx-auto h-16 w-16 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                  />
                </svg>
                <h3 className="mt-4 text-lg font-medium text-gray-900">Nog geen afspraken</h3>
                <p className="mt-2 text-sm text-gray-500">
                  Er zijn nog geen afspraken gemaakt in je beoordelingen.
                  <br />
                  Afspraken worden toegevoegd tijdens je beoordelingsgesprekken.
                </p>
              </div>
            )}

            {/* All Agreements (Alternative Flat View) */}
            {data.data.length > 0 && (
              <div className="mt-12">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Alle Afspraken (Chronologisch)</h2>
                <div className="space-y-3">
                  {data.data
                    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                    .map(agreement => (
                      <AgreementCard key={agreement.id} agreement={agreement} showEvaluation={true} />
                    ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
